import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';

import {
  type NavigationVariant,
  ProductCarouselDesktop,
  ProductCarouselMobile,
} from '~/components/isolated/Carousel';
import ProductCardSkeletonFactory from '~/components/ProductCardV3/skeletons/ProductCardSkeletonFactory';
import ProductCardM from '~/components/ProductCardV3/variants/ProductCardM/ProductCardM';
import {
  ProductListAnalyticsProvider,
  useCategoryBlockProductList,
} from '~/components/ProductListAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import type { Category } from '~/stores/CategoriesStore';

import { createCategoryUrl } from '../shared/utils';
import styles from './ProductsCarouselSliderOneBlock.module.scss';

interface ProductsCarouselSliderOneBlockProps {
  category: Category;
  navigationVariant?: NavigationVariant;
}

const ProductsCarouselSliderOneBlock = ({
  category,
  navigationVariant = 'header',
}: ProductsCarouselSliderOneBlockProps) => {
  const { pathname } = useLocation();
  const { isMobile } = useGlobal();
  const blockList = useCategoryBlockProductList(category, 'carousel');

  // Hide the block when there are no products and none are on the way —
  // home-page categories get their products with the categories tree, so
  // productsRequest may never run and never become fulfilled.
  if (!category.products.length && !category.productsRequest.isLoading) {
    return null;
  }

  const categoryId = category?.subcategory[0]?.id;

  if (!category.subcategory.length || !categoryId) {
    return null;
  }

  const linkTo =
    pathname !== '/search' ? createCategoryUrl({ id: categoryId }) : undefined;

  const products = category.products.slice(0, isMobile ? 10 : 15);
  const items = products.map((product, index) => ({
    id: product.id,
    node: <ProductCardM product={product} index={index} />,
  }));

  const skeletonComponent = (
    <div className={styles.skeletonContainer}>
      {Array.from({ length: 5 }, (_, i) => (
        <ProductCardSkeletonFactory key={`skeleton-${i}`} variant="m" />
      ))}
    </div>
  );

  const commonProps = {
    title: category.name,
    linkTo,
    categoryId,
    items,
    skeletonComponent: items.length === 0 ? skeletonComponent : undefined,
  };

  return (
    <ProductListAnalyticsProvider list={blockList}>
      <div className={styles.container}>
        {isMobile ? (
          <ProductCarouselMobile
            {...commonProps}
            itemClassName={styles.item}
            showViewAllOnMobile
          />
        ) : (
          <ProductCarouselDesktop
            {...commonProps}
            slideClassName={styles.slide}
            contentClassName={styles.carouselContent}
            navigationVariant={navigationVariant}
          />
        )}
      </div>
    </ProductListAnalyticsProvider>
  );
};

export default observer(ProductsCarouselSliderOneBlock);
